import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Telemetria } from "./../../types/types";
import TelemetriaDetailsModal from "./../../components/TelemetriaDetailsModal";
import TelemetriaFormModal from "./../../components/TelemetriaFormModal";
import styles from "./Telemetrias.module.css";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";

const API_URL = process.env.REACT_APP_API || "https://localhost:5001/api";

export default function TelemetriasPage() {
  const [telemetrias, setTelemetrias] = useState<Telemetria[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const token = useSelector((state: RootState) => state.auth.token);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  useSessionExpirationCheck(sessionExpiration, token);

  const carregarTelemetrias = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/Telemetria`);
      if (!res.ok) throw new Error(`Erro ${res.status} ao buscar telemetrias`);
      const data: Telemetria[] = await res.json();
      setTelemetrias(data);
    } catch (err) {
      console.error("Erro ao carregar telemetrias", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarTelemetrias();
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Remover esta telemetria?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API_URL}/Telemetria/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`Erro ${res.status} ao remover telemetria`);
      setTelemetrias((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error("Erro ao remover telemetria", err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingId(null);
    carregarTelemetrias();
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <span className={styles.eyebrow}>Instalações</span>
          <h1 className={styles.title}>Telemetrias</h1>
        </div>
        <button
          className={styles.createBtn}
          onClick={() => {
            setEditingId(null);
            setShowForm(true);
          }}
        >
          <Plus size={18} />
          Nova telemetria
        </button>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <Loader2 size={22} className={styles.spinner} />
          Carregando telemetrias...
        </div>
      ) : telemetrias.length === 0 ? (
        <div className={styles.emptyState}>
          Nenhuma telemetria cadastrada ainda.
        </div>
      ) : (
        <div className={styles.grid}>
          {telemetrias.map((t) => (
            <div
              key={t.id}
              className={styles.card}
              onClick={() => setSelectedId(t.id)}
            >
              <div className={styles.cardActions}>
                <button
                  className={styles.iconBtn}
                  title="Editar"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingId(t.id);
                    setShowForm(true);
                  }}
                >
                  <Pencil size={15} />
                </button>
                <button
                  className={`${styles.iconBtn} ${styles.iconBtnDangerHover}`}
                  title="Excluir"
                  disabled={deletingId === t.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(t.id);
                  }}
                >
                  <Trash2 size={15} />
                </button>
              </div>

              <span className={styles.cardTag}>
                {t.centrais.length}{" "}
                {t.centrais.length === 1 ? "central" : "centrais"}
              </span>

              <h3 className={styles.cardTitle}>{t.identificacao}</h3>
              <p className={styles.cardAddress}>{t.endereco}</p>

              <div className={styles.cardFooter}>
                <span>{t.operadora || "—"}</span>
                <span>{t.contato || "—"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedId !== null && (
        <TelemetriaDetailsModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

      {showForm && (
        <TelemetriaFormModal
          id={editingId}
          onClose={() => {
            setShowForm(false);
            setEditingId(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
}
